import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Accordion } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Shield, UserPlus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

import SecurityStatsCards from "../components/security/SecurityStatsCards";
import SecurityUsersAccordionItem from "../components/security/SecurityUsersAccordionItem";
import SecurityRolesAccordionItem from "../components/security/SecurityRolesAccordionItem";
import SecurityAuditAccordionItem from "../components/security/SecurityAuditAccordionItem";

const EMPTY_FORM = { username: "", email: "", first_name: "", last_name: "", phone: "", password: "", role_ids: [] };

function UserFormDialog({ open, mode, user, roles, onClose, onSuccess, toast }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && user) {
        const roleIds = roles.filter((r) => user.roles.includes(r.name)).map((r) => r.id);
        setForm({
          username: user.username || "",
          email: user.email || "",
          first_name: user.prenom || "",
          last_name: user.nom || "",
          phone: user.phone || "",
          password: "",
          role_ids: roleIds,
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, mode, user, roles]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleRole = (roleId) => {
    setForm((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.username.trim() || !form.email.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      toast.error(t("common.error"), { description: t("security.toasts.requiredFields") });
      return;
    }
    if (mode === "create" && !form.password.trim()) {
      toast.error(t("common.error"), { description: t("security.toasts.passwordRequired") });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        username: form.username.trim(),
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        role_ids: form.role_ids,
      };
      if (mode === "create") payload.password = form.password;

      if (mode === "create") {
        await api.post("v1/users/", payload);
        toast.success(t("security.toasts.userCreated"), { description: `${payload.first_name} ${payload.last_name}` });
      } else {
        await api.put(`v1/users/${user.id}/`, payload);
        toast.success(t("security.toasts.userUpdated"), { description: `${payload.first_name} ${payload.last_name}` });
      }
      onSuccess();
    } catch (err) {
      toast.error(t("common.error"), { description: err?.response?.data?.detail || t("common.operationError") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? t("security.userDialog.createTitle") : t("security.userDialog.editTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("security.userDialog.firstName")} *</Label>
              <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder={t("security.userDialog.firstName")} />
            </div>
            <div className="space-y-1">
              <Label>{t("security.userDialog.lastName")} *</Label>
              <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder={t("security.userDialog.lastName")} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>{t("security.userDialog.username")} *</Label>
            <Input value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="ex: john.doe" autoCapitalize="none" />
          </div>

          <div className="space-y-1">
            <Label>{t("security.userDialog.email")} *</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@example.com" type="email" />
          </div>

          <div className="space-y-1">
            <Label>{t("security.userDialog.phone")}</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+XX XXX XXX XXX" />
          </div>

          {mode === "create" && (
            <div className="space-y-1">
              <Label>{t("security.userDialog.password")} *</Label>
              <div className="relative">
                <Input
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder={t("security.userDialog.passwordPlaceholder")}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          {roles.length > 0 && (
            <div className="space-y-2">
              <Label>{t("security.userDialog.roles")}</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-3">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={form.role_ids.includes(role.id)}
                      onCheckedChange={() => toggleRole(role.id)}
                    />
                    <label htmlFor={`role-${role.id}`} className="text-sm cursor-pointer">
                      <span className="font-medium">{role.name}</span>
                      {role.description && <span className="text-muted-foreground ml-2">— {role.description}</span>}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t("security.userDialog.saving") : mode === "create" ? t("security.userDialog.create") : t("security.userDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_ROLE_FORM = { name: "", description: "" };

function RoleFormDialog({ open, mode, role, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_ROLE_FORM);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/v1/users/roles/permissions").then((r) => setAllPermissions(r.data)).catch(() => {});
    if (mode === "edit" && role) {
      setForm({ name: role.name || "", description: role.description || "" });
      setSelectedPermIds(role.permissions.map((p) => p.id).filter(Boolean));
    } else {
      setForm(EMPTY_ROLE_FORM);
      setSelectedPermIds([]);
    }
  }, [open, mode, role]);

  const togglePerm = (id) =>
    setSelectedPermIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const byModule = allPermissions.reduce((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module].push(p);
    return acc;
  }, {});

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error(t("security.roles.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description.trim(), permission_ids: selectedPermIds };
      if (mode === "create") {
        await api.post("/v1/users/roles/", payload);
        toast.success(t("security.roles.created"));
      } else {
        await api.put(`/v1/users/roles/${role.id}/`, payload);
        toast.success(t("security.roles.updated"));
      }
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("security.roles.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? t("security.roles.newRole") : t("security.roles.editRole", { name: role?.name })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("security.roles.nameLabel")}</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="ex: RH_MANAGER" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder={t("security.roles.descPlaceholder")} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Permissions ({selectedPermIds.length} sélectionnées)</Label>
            <div className="border rounded p-3 space-y-3 max-h-64 overflow-y-auto">
              {Object.entries(byModule).map(([module, perms]) => (
                <div key={module}>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{module}</p>
                  <div className="grid grid-cols-2 gap-1">
                    {perms.map((p) => (
                      <div key={p.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`perm-${p.id}`}
                          checked={selectedPermIds.includes(p.id)}
                          onCheckedChange={() => togglePerm(p.id)}
                        />
                        <label htmlFor={`perm-${p.id}`} className="text-xs cursor-pointer" title={p.description}>
                          {p.action}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {allPermissions.length === 0 && <p className="text-sm text-muted-foreground">{t("security.roles.loadingPermissions")}</p>}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t("security.roles.saving") : mode === "create" ? t("common.create") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AUDIT_PLACEHOLDER = [
  { id: 1, user: "admin", action: "LOGIN", module: "auth", timestamp: new Date().toISOString(), ip_address: "—", resultat: "SUCCESS" },
];

const SecurityRoles = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [userDialog, setUserDialog] = useState({ open: false, user: null, mode: "create" });
  const [roleDialog, setRoleDialog] = useState({ open: false, role: null, mode: "create" });
  const [twoFactorDialog, setTwoFactorDialog] = useState({ open: false, user: null });

  const [usersQ, rolesQ] = useQueries({
    queries: [
      { queryKey: ["security-users"], queryFn: () => api.get("/v1/users/").then((r) => Array.isArray(r.data) ? r.data : []) },
      { queryKey: ["security-roles"], queryFn: () => api.get("/v1/users/roles").then((r) => Array.isArray(r.data) ? r.data : []) },
    ],
  });

  useEffect(() => {
    if (usersQ.isError || rolesQ.isError) toast.error(t("common.error"), { description: t("security.toasts.loadError") });
  }, [usersQ.isError, rolesQ.isError]);

  const users = usersQ.data ?? [];
  const roles = rolesQ.data ?? [];
  const loading = usersQ.isPending || rolesQ.isPending;

  const stats = useMemo(() => ({
    totalUsers: users.length,
    activeUsers: users.filter((u) => u.statut === "ACTIF").length,
    adminUsers: users.filter((u) => u.roles?.some((r) => r === "ADMIN" || r === "ADMIN_IT")).length,
    twoFactorEnabled: users.filter((u) => u.otp_enabled).length,
    suspendedUsers: users.filter((u) => u.statut === "SUSPENDU").length,
    lastLogin: users.reduce((latest, u) =>
      u.derniere_connexion && new Date(u.derniere_connexion) > new Date(latest || 0)
        ? u.derniere_connexion : latest, null),
  }), [users]);

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === "ACTIF" ? "INACTIVE" : "ACTIVE";
      await api.patch(`v1/users/${userId}/status/`, { status: newStatus });
      queryClient.invalidateQueries({ queryKey: ["security-users"] });
      toast.success(t("security.toasts.statusUpdated"));
    } catch (error) {
      toast.error(t("common.error"), { description: error.response?.data?.detail || t("common.operationError") });
    }
  };

  const resetPassword = async (userId) => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const tmp = Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("") + "1A";
    try {
      await api.post(`v1/users/${userId}/reset-password/`, { new_password: tmp });
      toast.success(t("security.toasts.passwordReset"), { description: `${t("security.toasts.passwordReset")} : ${tmp}`, duration: 15000 });
    } catch (err) {
      toast.error(t("common.error"), { description: err?.response?.data?.detail || t("security.roles.resetError") });
    }
  };

  const twoFactorRate = stats.totalUsers > 0 ? (stats.twoFactorEnabled / stats.totalUsers) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("security.title")}</h1>
          <p className="text-muted-foreground">{t("security.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setUserDialog({ open: true, user: null, mode: "create" })}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t("security.newUser")}
          </Button>
          <Button onClick={() => setRoleDialog({ open: true, role: null, mode: "create" })} variant="outline">
            <Shield className="mr-2 h-4 w-4" />
            {t("security.newRole")}
          </Button>
        </div>
      </div>

      <SecurityStatsCards stats={stats} twoFactorRate={twoFactorRate} />

      <Accordion type="single" defaultValue="users" className="w-full">
        <SecurityUsersAccordionItem
          users={users}
          totalUsers={stats.totalUsers}
          toggleUserStatus={toggleUserStatus}
          resetPassword={resetPassword}
          setUserDialog={setUserDialog}
          setTwoFactorDialog={setTwoFactorDialog}
        />
        <SecurityRolesAccordionItem roles={roles} setRoleDialog={setRoleDialog} />
        <SecurityAuditAccordionItem auditLogs={AUDIT_PLACEHOLDER} />
      </Accordion>

      <UserFormDialog
        open={userDialog.open}
        mode={userDialog.mode}
        user={userDialog.user}
        roles={roles}
        onClose={() => setUserDialog({ open: false, user: null, mode: "create" })}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["security-users"] });
          setUserDialog({ open: false, user: null, mode: "create" });
        }}
        toast={toast}
      />

      <RoleFormDialog
        open={roleDialog.open}
        mode={roleDialog.mode}
        role={roleDialog.role}
        onClose={() => setRoleDialog({ open: false, role: null, mode: "create" })}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["security-roles"] });
          setRoleDialog({ open: false, role: null, mode: "create" });
        }}
      />
    </div>
  );
};

export default SecurityRoles;
